import {
  SOLUTION_DOMAINS,
  SOLUTION_DOMAINS_NL,
  SolutionDomain,
  getSolutionsFromDomain,
} from '@energiebespaarders/constants';
import {
  Box,
  Button,
  Container,
  Flex,
  Icon,
  Image,
  Spacer,
  theme,
} from '@energiebespaarders/symbols';
import { Book, Center, Medium, Small } from '@energiebespaarders/symbols/helpers';
import { useIsMobile, useIsTablet } from '@energiebespaarders/symbols/hooks';
import { ChatBubbles, Check, Pen, Settings } from '@energiebespaarders/symbols/icons/solid';
import { mediaMax } from '@energiebespaarders/symbols/styles/breakpoints';
import { tint } from '@energiebespaarders/symbols/styles/mixins';
import Head from 'next/head';
import illustrations from 'public/img/illustrations/cutouts/solutionIllustrations';
import marginCompare from 'public/img/installerSignUpLanding/marginCompare-min.png';
import LogoBlack from 'public/img/logo_WEB_dark.svg';
import LogoWhite from 'public/img/logo_WEB_white.svg';
import styled from 'styled-components';
import PartnerSlider from '~/components/HomePage/PartnerSlider';
import ImageCard from '~/components/ImageCard';
import HeroChecks from '~/components/InstallerSignUpLanding/HeroChecks';
import LogoBubbles from '~/components/InstallerSignUpLanding/LogoBubbles';
import TestimonialCard from '~/components/InstallerSignUpLanding/TestimonialCard';
import {
  customerTestimonials,
  installerTestimonials,
} from '~/components/InstallerSignUpLanding/installerTestimonials';
import type { Page } from '~/types/next';
import { marketingImages } from '~/utils/constants';
import { shuffleArray } from '~/utils/shuffleArray';
import ChargingBoxIllustration from '/public/img/illustrations/cutouts/laadpaal.png';

export const StyledInstallerCTAButton = styled(Button)`
  border-top: 1px solid ${theme.colors.orangeDark};
  border-left: 1px solid ${theme.colors.orangeDark};
  border-right: 1px solid ${theme.colors.orangeDark};
  border-bottom: 2px solid ${theme.colors.orangeDarker};
  border-radius: '4px';

  &:hover {
    background: #b77a00;
  }
`;

const OverrideFontSize = styled.div`
  ${mediaMax.lg} {
    & * p {
      font-size: 1rem !important;
      line-height: 1.5;
    }
  }
`;

const InstallerSignUpLanding: Page = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const translateSolutionDomain = (solutionDomain: SolutionDomain) => {
    if (solutionDomain === SolutionDomain.RoofInsulation) {
      return 'Dakisolatie en sedum';
    }
    if (solutionDomain === SolutionDomain.Heating) {
      return 'CV en warmtepompen';
    }
    if (solutionDomain === SolutionDomain.FloorInsulation) {
      return 'Vloer- en bodemisolatie';
    }
    // HACK:
    if (solutionDomain === SolutionDomain.Miscellaneous) {
      return 'Laadpalen';
    }
    return SOLUTION_DOMAINS_NL[solutionDomain];
  };
  return (
    <OverrideFontSize>
      <Head>
        <title key="title">Word installatiepartner van Energiebespaarders</title>
      </Head>

      <Flex flexWrap="wrap" style={{ background: theme.colors.gold }}>
        {isMobile ? (
          <Box width={1} pb={[2, 2, 4]} style={{ position: 'relative' }}>
            <Image
              src={marketingImages.wallInsulationPortrait}
              alt="Isolatiespecialist"
              masked
              maskWidth="100%"
              maskHeight="30vh"
            />
            <Image
              src={LogoWhite}
              alt="Energiebespaarders logo"
              style={{ position: 'absolute', top: 24, left: 24, width: 240, zIndex: 1 }}
            />
          </Box>
        ) : (
          <Box width={1} px={[4, 4, 4, 4, 0]} py={[2, 2, 6]}>
            <Container>
              <Image
                src={isMobile ? LogoWhite : LogoBlack}
                alt="Energiebespaarders logo"
                style={{ width: 240 }}
              />
            </Container>
          </Box>
        )}

        <Box width={1} px={[4, 4, 4, 4, 0]}>
          <Container>
            <Flex flexWrap="wrap" mx={[-2, -2, -4]} alignItems="center">
              <Box width={[1, 1, 1 / 2]} px={[2, 2, 4]} pt={[4, 4, 2]} pb={[4, 4, 2]}>
                <h1 style={{ lineHeight: 1.2 }}>
                  <Medium>Jij installeert duurzame producten.</Medium> <br />
                  Wij regelen de rest.
                </h1>
                <HeroChecks />
                <StyledInstallerCTAButton
                  href="https://tally.so/r/nPy94x"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Meld je aan als installatiepartner"
                  label="Meld je aan als installatiepartner"
                  bgColor="white"
                  color={theme.colors.orangeDark}
                  fontSize={4}
                  px={4}
                  fluid={isMobile}
                />
              </Box>
              {!isMobile && (
                <Box width={[1, 1, 1 / 2]} px={[2, 2, 4]} pt={[4, 4, 2]}>
                  <Image
                    src="https://www.datocms-assets.com/50550/1731402831-herocollage-min.png?auto=compress,format&w=600"
                    alt="hoi"
                    width="100%"
                    radius="6px"
                  />
                </Box>
              )}
            </Flex>
          </Container>
        </Box>
        <Box width={1} pt={2}>
          <Box px={[4, 4, 4, 4, 0]}>
            <Container textAlign={isMobile ? 'center' : 'right'} mb={-5}>
              <Small>Wij zijn onafhankelijk partner van o.a.</Small>
            </Container>
          </Box>
          <PartnerSlider />
        </Box>
      </Flex>

      <Flex
        flexWrap="wrap"
        px={[4, 4, 4, 4, 0]}
        style={{ background: tint(0.33, theme.colors.gold) }}
        py={8}
      >
        <Box width={1}>
          <Container textAlign="center">
            <h2>We bieden opdrachten voor de installatie van</h2>
            <Flex flexWrap="wrap" mx={[-2, -2, -4]} alignItems="flex-start" justifyContent="center">
              {SOLUTION_DOMAINS.map((sd, index) => (
                <Box
                  key={`ill-${index}`}
                  width={[1 / 2, 1 / 3, 1 / 4, 1 / 7]}
                  px={[2, 2, 4]}
                  py={[2, 2, 2, 4]}
                  textAlign="center"
                >
                  <Image
                    src={
                      sd === SolutionDomain.Miscellaneous
                        ? ChargingBoxIllustration
                        : illustrations[getSolutionsFromDomain(sd)[0]]
                    }
                    alt={SOLUTION_DOMAINS_NL[sd]}
                    style={{
                      width: isTablet ? '150px' : '100%',
                      border: `3px solid ${theme.colors.gold}`,
                    }}
                    radius="100%"
                  />
                  <p style={{ hyphens: 'auto', wordBreak: 'keep-all' }}>
                    <Small>
                      <Book>{translateSolutionDomain(sd)}</Book>
                    </Small>
                  </p>
                </Box>
              ))}
            </Flex>
          </Container>
        </Box>
      </Flex>

      <Flex flexWrap="wrap">
        <Box width={1} px={[4, 4, 4, 4, 0]} py={8}>
          <Container textAlign="center" size="md">
            <h2>Met opdrachten van Energiebespaarders kun je écht focussen op installeren</h2>
          </Container>

          <Container>
            <Flex
              flexWrap="wrap"
              mx={[-2, -2, -4]}
              style={{
                borderBottom: isMobile || isTablet ? `1px solid ${theme.colors.gray}` : 'none',
              }}
              pb={[4, 4, 0]}
              mb={[4, 4, 0]}
            >
              <Box width={[1, 1, 1 / 2]} py={[2, 2, 4]} px={[2, 2, 4]}>
                <Image
                  src={marketingImages.markEndoscope}
                  alt="Onze energieadviseur controleert de spouw"
                  masked={isMobile}
                  maskWidth="100%"
                  maskHeight="360px"
                  width="100%"
                  radius="6px"
                />
              </Box>
              <Box width={[1, 1, 1 / 2]} py={[2, 2, 4]} px={[2, 2, 4]}>
                <h3>94,6% van onze opdrachten zijn definitief</h3>
                <Icon icon={Check} fill="green" mr={1} width="1.25em" verticalAlign="sub" />
                <Medium>Klanten van Energiebespaarders zijn goed geïnformeerd</Medium>
                <p>
                  Aan een opdracht gaat bij ons altijd een maatadvies vooraf. Dat wil zeggen dat
                  onze energie adviseur het meetwerk voor de geoffereerde producten al op locatie
                  heeft uitgevoerd om tot een kloppende offerte te komen.
                </p>

                <Spacer vertical amount={4} />

                <Icon icon={Check} fill="green" mr={1} width="1.25em" verticalAlign="sub" />
                <Medium>Klanten van Energiebespaarders zijn al akkoord met de offerte</Medium>
                <p>
                  Wij bieden de klant een offerte met jouw prijzen. Voordat de opdracht aan jou
                  wordt aangeboden heeft de klant altijd al een akkoord gegeven op een (voorlopige)
                  offerte.
                </p>
              </Box>
            </Flex>

            <Flex
              flexWrap="wrap"
              flexDirection={['column-reverse', 'column-reverse', 'row']}
              mx={[-2, -2, -4]}
              style={{
                borderBottom: isMobile || isTablet ? `1px solid ${theme.colors.gray}` : 'none',
              }}
              pb={[4, 4, 0]}
              mb={[4, 4, 0]}
            >
              <Box width={[1, 1, 1 / 2]} py={[2, 2, 4]} px={[2, 2, 4]}>
                <h3>Onderaan de streep meer verdienen</h3>
                <Icon icon={Check} fill="green" mr={1} width="1.25em" verticalAlign="sub" />
                <Medium>Een hogere marge per opdracht</Medium>
                <p>
                  Je betaalt bij ons niet voor de opdrachten. We vragen wel om een lagere
                  installatieprijs. Dat vinden we eerlijk, want we nemen een gedeelte van het werk
                  voor onze rekening. Het belangrijkste is dat je onder aan de streep meer overhoudt
                  en minder tijd kwijt bent aan randzaken.
                </p>
              </Box>
              <Box width={[1, 1, 1 / 2]} py={[2, 2, 4]} px={[2, 2, 4]}>
                <Box
                  px={4}
                  pt={4}
                  style={{ background: theme.colors.grayLighter, borderRadius: '6px' }}
                >
                  <Box width={[1, 2 / 3, 1 / 2, 2 / 3]} textAlign="center" mx="auto">
                    <Image
                      src={marginCompare}
                      alt="Je ontvangt een hogere marge per opdracht"
                      width="100%"
                    />
                  </Box>
                </Box>
              </Box>
            </Flex>

            <Flex flexWrap="wrap" mx={[-2, -2, -4]}>
              <Box width={[1, 1, 1 / 2]} py={[2, 2, 4]} px={[2, 2, 4]}>
                <Image
                  src={marketingImages.wallInsulationLadders}
                  alt="De spouw wordt geïsoleerd"
                  masked={isMobile}
                  maskWidth="100%"
                  maskHeight="360px"
                  width="100%"
                  radius="6px"
                  style={{ opacity: 0.85 }}
                />
              </Box>
              <Box width={[1, 1, 1 / 2]} py={[2, 2, 4]} px={[2, 2, 4]}>
                <h3>Minder tijd kwijt aan randzaken</h3>
                <Icon icon={Check} fill="green" mr={1} width="1.25em" verticalAlign="sub" />
                <Medium>Minder schouwen</Medium>
                <p>
                  Onze energieadviseurs proberen bij het maken van offertes al zo goed mogelijk een
                  inschatting te maken van het uit te voeren werk. Zij doen hier voor het nodige
                  meet - en schouw werk. Daardoor kan jij gelijk aan de slag met een definitieve
                  installatie.
                </p>

                <Spacer vertical amount={4} />

                <Icon icon={Check} fill="green" mr={1} width="1.25em" verticalAlign="sub" />
                <Medium>Minder werk aan administratie, planning en klantcontact</Medium>
                <p>
                  Wij zijn de aannemer van de verduurzaming voor de klant. Daarmee nemen we het
                  grootste gedeelte van de administratie voor onze rekening. We houden de klant goed
                  op de hoogte gedurende het proces. Ook factureren we de klant en zorgen er voor
                  dat jij netjes op tijd je geld hebt.
                </p>
              </Box>
            </Flex>
          </Container>
        </Box>

        <Box width={1} px={[4, 4, 4, 4, 0]} py={8} style={{ background: theme.colors.grayLighter }}>
          <Container size="md" textAlign="center">
            <Center block>
              <h2>Zo werken we samen</h2>
            </Center>
            <p>
              We proberen het proces zo efficiënt mogelijk in te richten. Wij kunnen de klant
              adviseren over verschillende oplossingen en direct ook een kloppende offerte
              aanbieden. Als de klant akkoord geeft hoef jij alleen nog maar te installeren.
            </p>
          </Container>
          <Container>
            <Flex flexWrap="wrap" mx={[-2, -2, -2]} justifyContent="center">
              <Box
                width={[1, 1 / 2, 1 / 2, 1 / 4]}
                py={4}
                px={[2, 2, 2]}
                style={{ display: 'flex' }}
              >
                <ImageCard
                  imageSrc={marketingImages.markGesturing}
                  alt="Onze adviseur legt de klant iets uit"
                  height="200px"
                  title="We adviseren de klant"
                  headingType="h3"
                  content={
                    <p>
                      Onze adviseur meet de woning van de klant op en maakt offertes voor geschikte
                      oplossingen.
                    </p>
                  }
                />
              </Box>
              <Box
                width={[1, 1 / 2, 1 / 2, 1 / 4]}
                py={4}
                px={[2, 2, 2]}
                style={{ display: 'flex' }}
              >
                <ImageCard
                  imageSrc={marketingImages.laptopCloseup}
                  alt="De klant bekijkt haar geadviseerde offertes"
                  height="200px"
                  title="De klant koopt een product"
                  headingType="h3"
                  content={
                    <p>
                      Als de klant een akkoord geeft op een offerte gaat er automatisch een voorstel
                      voor een opdracht naar jou.
                    </p>
                  }
                />
              </Box>
              <Box
                width={[1, 1 / 2, 1 / 2, 1 / 4]}
                py={4}
                px={[2, 2, 2]}
                style={{ display: 'flex' }}
              >
                <ImageCard
                  imageSrc={marketingImages.installerBus}
                  alt=""
                  height="200px"
                  title="Jij ontvangt de opdracht"
                  headingType="h3"
                  content={
                    <p>
                      Jij ontvangt een opdracht met alle benodigde informatie om de opdracht uit te
                      voeren. Als je deze accepteert kun je direct een afspraak inplannen.
                    </p>
                  }
                />
              </Box>
              <Box
                width={[1, 1 / 2, 1 / 2, 1 / 4]}
                py={4}
                px={[2, 2, 2]}
                style={{ display: 'flex' }}
              >
                <ImageCard
                  imageSrc={marketingImages.liftingPv}
                  alt=""
                  height="200px"
                  title="Jij installeert, wij doen de rest"
                  headingType="h3"
                  content={
                    <p>
                      Je hoeft alleen te installeren. Wij zorgen voor het contact en administratie.
                      Je ontvangt de betaling als de installatie is afgetekend door de klant.
                    </p>
                  }
                />
              </Box>
            </Flex>
          </Container>
        </Box>

        <Box width={1} px={[4, 4, 4, 4, 0]} py={8}>
          <Container size="lg" textAlign="center">
            <LogoBubbles />
          </Container>
          <Container size="md" textAlign="center" pt={2}>
            <h2>Het breedste assortiment van Nederland</h2>
            <p>
              Omdat we veel klanten helpen met het verduurzamen van hun woning kunnen we op grote
              schaal producten inkopen. We kunnen ook voor jouw installaties de producten leveren.
            </p>
          </Container>
        </Box>

        <Box width={1} px={[4, 4, 4, 4, 0]} py={8} style={{ background: theme.colors.grayLighter }}>
          <Container>
            <Center block>
              <h2>Zo starten we onze samenwerking</h2>
            </Center>
            <Flex flexWrap="wrap" mx={[-2, -2, -4]}>
              <Box width={[1, 1, 1 / 3]} py={4} px={[2, 2, 4]}>
                <Icon icon={Pen} fill={theme.colors.goldDarker} width="2em" pb={2} />
                <h3>1. Aanmelden</h3>
                <p>
                  Om te kunnen inschatten of we kunnen samenwerken hebben we eerst wat informatie
                  nodig. We willen bijvoorbeeld weten welke producten je installeert en hoe veel
                  opdrachten je per week wilt ontvangen.
                </p>
              </Box>
              <Box width={[1, 1, 1 / 3]} py={4} px={[2, 2, 4]}>
                <Icon icon={ChatBubbles} fill={theme.colors.goldDarker} width="2em" pb={2} />
                <h3>2. Kennismakingsgesprek</h3>
                <p>
                  We bekijken je aanmelding en plannen een gesprek in. Samen kijken we of we elkaar
                  kunnen versterken. Is het geen match? Even goede vrienden!
                </p>
              </Box>
              <Box width={[1, 1, 1 / 3]} py={4} px={[2, 2, 4]}>
                <Icon icon={Settings} fill={theme.colors.goldDarker} width="2em" pb={2} />
                <h3>3. Afspraken maken</h3>
                <p>
                  We spreken af hoe we gaan samenwerken. Dat komt kortgezegd neer op een afspraak
                  over hoe veel opdrachten je voor ons wilt uitvoeren en welke prijs je daar voor
                  rekent.
                </p>
              </Box>
              <Box width={1} py={4} px={[2, 2, 4]} textAlign="center">
                <Button
                  href="https://tally.so/r/nPy94x"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Meld je aan als installatiepartner"
                  label="Meld je aan als installatiepartner"
                  bgColor="yellow"
                  color="grayBlack"
                  px={5}
                />
              </Box>
            </Flex>
          </Container>
        </Box>
      </Flex>

      <Flex flexWrap="wrap" px={[4, 4, 4, 4, 0]}>
        <Box width={1} py={8}>
          <Container>
            <Center block>
              <h2>Wie zijn Energiebespaarders?</h2>
            </Center>
            <Flex flexWrap="wrap" mx={[-2, -2, -4]}>
              <Box width={[1, 1, 1 / 2]} py={[2, 2, 4]} px={[2, 2, 4]}>
                <Image
                  src={marketingImages.markMeasuringGlass}
                  alt="Onze energieadviseur meet de ramen op"
                  maskWidth="100%"
                  masked
                  maskHeight={isMobile ? '360px' : '100%'}
                  radius="6px"
                />
              </Box>
              <Box width={[1, 1, 1 / 2]} py={[2, 2, 4]} px={[2, 2, 4]}>
                <h3>
                  Energiebespaarders maakt energiepositief wonen simpel en toegankelijk voor
                  iedereen.
                </h3>
                <p>
                  Er zijn veel duurzame oplossingen voor energiegebruik. Wij brengen ze op één
                  platform bij elkaar samen, zodat klanten bij ons voor de volledige verduurzaming
                  terecht kunnen.
                </p>

                <p>
                  We leveren opdrachten aan partner- installatiebedrijven. Altijd definitief en
                  kant-en-klaar. We maken daarmee het werk voor alle type uitvoerders makkelijker,
                  goedkoper en efficiënter. Zo versnellen we de transitie naar een toekomst waarin
                  energiepositief wonen de nieuwe norm is.
                </p>
              </Box>
            </Flex>
          </Container>
        </Box>
      </Flex>

      <Flex flexWrap="wrap" px={[4, 4, 4, 4, 0]} pb={8}>
        <Box width={1}>
          <Container>
            <Flex flexWrap="wrap" mx={[-2, -2, -4]}>
              <Box width={[1, 1, 1 / 2]} px={[2, 2, 4]}>
                <h2>Klanten over ons</h2>
                <Flex flexWrap="wrap" mx={-2}>
                  {[...new Set(shuffleArray(customerTestimonials))].slice(0, 2).map((t, i) => (
                    <Box key={`${t.name}-${i}`} width={[1, 1, 1 / 2]} px={2} py={[2, 2, 0]}>
                      <TestimonialCard testimonial={t} />
                    </Box>
                  ))}
                </Flex>
              </Box>
              <Box width={[1, 1, 1 / 2]} px={[2, 2, 4]} pt={[4, 4, 0]}>
                <h2>Partners over ons</h2>
                <Flex flexWrap="wrap" mx={-2}>
                  {[...new Set(shuffleArray(installerTestimonials))].slice(0, 2).map((t, i) => (
                    <Box key={`${t.name}-${i}`} width={[1, 1, 1 / 2]} px={2} py={[2, 2, 0]}>
                      <TestimonialCard testimonial={t} />
                    </Box>
                  ))}
                </Flex>
              </Box>
            </Flex>
          </Container>
        </Box>
      </Flex>
    </OverrideFontSize>
  );
};

export default InstallerSignUpLanding;
